import { FieldInfo } from '@/components/FieldInfo';
import type { OrgFormApi } from '@/config/organizations/formTypes';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../styles.module.css';
import { gridProps, stackProps } from '../constants';
import styles from '../styles.module.css';

type BasicInfoSectionProps = {
  form: OrgFormApi;
  isReadOnly?: boolean;
  /** Alias is immutable once an organization is created (POR-396) — false in edit mode regardless of isReadOnly. */
  isAliasEditable?: boolean;
  /** Force-show validation errors regardless of per-field touched state (e.g. after a blocked submit attempt). */
  showErrors?: boolean;
};

function BasicInfoSectionComponent({
  form,
  isReadOnly = false,
  isAliasEditable = true,
  showErrors,
}: BasicInfoSectionProps) {
  const { t } = useTranslation('orgs');

  // The form prop should implement the Field API used below. Use a safe type-guard
  // instead of an `as any` cast to check for the presence of `Field` at runtime.
  if (!form || typeof (form as unknown as { Field?: unknown }).Field !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[BasicInfoSectionComponent] form prop is missing or invalid:', form);
    }
    return null;
  }

  return (
    <Stack {...stackProps}>
      <Text as="h3" className={orgStyles.sectionHeading}>
        {t('create_org_section_basic')}
      </Text>

      <Grid className={styles.basicInfoSectionGrid} {...gridProps}>
        <Stack {...stackProps} gap={10}>
          {/* Name Field — required/length/character validation lives in the form-level Zod schema (see createOrgSchema). */}
          <form.Field name="name">
            {field => (
              <>
                <Input
                  autoComplete="off"
                  className={orgStyles.uiInput}
                  data-testid="input-name"
                  id="name"
                  readOnly={isReadOnly}
                  label={{
                    children: t('create_org_name_label'),
                    htmlFor: 'name',
                    id: 'label-name',
                  }}
                  name="name"
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_org_name_placeholder')}
                />
                <FieldInfo field={field} showErrors={showErrors || field.state.meta.isTouched} />
              </>
            )}
          </form.Field>

          {/* Alias Field */}
          <form.Field name="alias">
            {field => (
              <>
                <Input
                  autoComplete="off"
                  className={orgStyles.uiInput}
                  data-testid="input-alias"
                  id="alias"
                  readOnly={isReadOnly || !isAliasEditable}
                  label={{
                    children: t('create_org_alias_label'),
                    htmlFor: 'alias',
                    id: 'label-alias',
                  }}
                  name="alias"
                  value={field.state.value ?? ''}
                  onChange={e => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_org_alias_placeholder')}
                />
                <FieldInfo field={field} showErrors={showErrors || field.state.meta.isTouched} />
              </>
            )}
          </form.Field>
        </Stack>

        {/* Address Field */}
        <form.Field name="address">
          {field => (
            <>
              <Textarea
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid="input-address"
                id="address"
                isReadOnly={isReadOnly}
                label={{
                  children: t('create_org_address_label'),
                  htmlFor: 'address',
                  id: 'label-address',
                }}
                name="address"
                value={field.state.value ?? ''}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_address_placeholder')}
                rows={7}
              />
              <FieldInfo field={field} showErrors={showErrors || field.state.meta.isTouched} />
            </>
          )}
        </form.Field>
      </Grid>
    </Stack>
  );
}

export const BasicInfoSection = memo(BasicInfoSectionComponent);
